import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { auth } from "../../../utils/auth";
import { formatCurrency } from "../../../utils/helpers";
import { request } from "../../../utils/request";
import { buildNotasVentasParams, buildRecibosParams, calculateVentasSummaries, processFactura } from "./cuadreUtils";

const useData = () => {
    const location = useLocation();
	const queryParams = new URLSearchParams(location.search);

	const [finishedCallCount, setFinishedCallCount] = useState(0);

	const [facturas, setFacturas] = useState([]);
	const [recibos, setRecibos] = useState([]);
    const [notas, setNotas] = useState([]);

	const [compras, setCompras] = useState([]);
	const [notasCompra, setNotasCompra] = useState([]);

	const [cuentasPorPagar, setCuentasPorPagar] = useState([]);

	const [summaries, setSummaries] = useState({
		ventas: {
			efectivo: 0,
			transferencia: 0,
			cheque: 0,
			tarjeta: 0,
			'tarjeta-debito': 0,
			deposito: 0,
			otro: 0,
			pagada: 0,
			contado: 0,
			contraEntrega: 0,
			credito: 0,
		},
		compras: {
			producto_contado: 0,
			producto_credito: 0,
			producto_nota: 0,
			item_contado: 0,
			item_credito: 0,
			item_nota: 0,
			suministro_contado: 0,
			suministro_credito: 0,
			suministro_nota: 0,
			contado: 0,
			credito: 0,
			nota: 0,
			formaPago: {
				efectivo: 0,
				transferencia: 0,
				tarjeta: 0,
				credito: 0,
				permuta: 0,
				nota: 0,
				mixto: 0,
			},
		},
	});

	const getFacturas = useCallback(() => {
		const searchString =
			location.search === ""
				? "?paginate=100000"
				: location.search + "&paginate=1000000";
		const hasta = dayjs(queryParams.get("hasta")).add(1, 'days');
		request(`facturas${searchString}`).then((res) => {
			setFacturas(res.data.map(factura => processFactura(factura, hasta)));
			setFinishedCallCount((count) => count + 1);
		});
	}, []);

	const getRecibos = useCallback(() => {
		const searchString = new URLSearchParams(buildRecibosParams(queryParams));
		request(`recibos-ingreso?${searchString.toString()}`).then((res) => {
			setRecibos(res.data);
			setFinishedCallCount((count) => count + 1);
		});
	}, []);

	const getNotasCredito = useCallback(() => {
		const searchString = new URLSearchParams(buildNotasVentasParams(queryParams));
		request(`notas-credito?${searchString.toString()}`).then((res) => {
			setNotas(res.data);
			setFinishedCallCount((count) => count + 1);
		});
	}, []);

	const getCompras = useCallback(() => {
		const params = {
			desde: queryParams.get("desde") ? queryParams.get("desde") : "",
			hasta: queryParams.get("hasta") ? queryParams.get("hasta") : "",
		};
		const searchString = `?desde=${params.desde}&hasta=${params.hasta}&paginate=1000000`;
		request(`compras${searchString}`).then((res) => {
			setCompras(res.data);
			setFinishedCallCount((count) => count + 1);
		});
	}, []);
	const getNotasCompra = useCallback(() => {
		const params = {
			desde: queryParams.get("desde") ? queryParams.get("desde") : "",
			hasta: queryParams.get("hasta") ? queryParams.get("hasta") : "",
		};
		const searchString = `?tipo=compra&desde=${params.desde}&hasta=${params.hasta}&paginate=1000000`;
		request(`notas-credito${searchString}`).then((res) => {
			setNotasCompra(res.data);
			setFinishedCallCount((count) => count + 1);
		});
	}, []);

	const getCuentasPorPagar = useCallback(() => {
		const params = {
			desde: queryParams.get("desde") ? queryParams.get("desde") : "",
			hasta: queryParams.get("hasta") ? queryParams.get("hasta") : "",
		};
		const searchString = `?desde=${params.desde}&hasta=${params.hasta}&paginate=1000000`;
		request(`cuentas-por-pagar${searchString}`).then((res) => {
			setCuentasPorPagar(res.data);
			setFinishedCallCount((count) => count + 1);
		});
	}, []);


    const calculateSummaries = useCallback(() => {
		const ventasSummaries = calculateVentasSummaries(facturas, recibos);

		// Summary Compras
		const summaryCompras = {
			producto_contado: 0,
			producto_credito: 0,
			producto_nota: 0,
			item_contado: 0,
			item_credito: 0,
			item_nota: 0,
			suministro_contado: 0,
			suministro_credito: 0,
			suministro_nota: 0,
		};
		const formaPago = {
			efectivo: 0,
			transferencia: 0,
			tarjeta: 0,
			'tarjeta-debito': 0,
			credito: 0,
			permuta: 0,
			nota: 0,
			mixto: 0,
		};
		for (const compra of compras) {
			const tipoPago = compra.tipoPago === "04 - COMPRA A CREDITO" ? "credito"
				: (compra.tipoPago === "06 - NOTA DE CREDITO" ? "nota" : "contado");

			const key = `${compra.tipo}_${tipoPago}`;

			if (Object.hasOwnProperty.call(summaryCompras, key)) {
				summaryCompras[key] += compra.total - compra.porPagar;
			}

			switch (compra.tipoPago) {
				case "01 - EFECTIVO":
					formaPago.efectivo += compra.total - compra.porPagar;
					break;
				case "02 - CHEQUES/TRANSFERENCIAS":
					formaPago.transferencia += compra.total - compra.porPagar;
					break;
				case "03 - TARJETA DE CREDITO/DEBITO":
					formaPago.tarjeta += compra.total - compra.porPagar;
					break;
				case "04 - COMPRA A CREDITO":
					formaPago.credito += compra.total - compra.porPagar;
					break;
				case "05 - PERMUTA":
					formaPago.permuta += compra.total - compra.porPagar;
					break;
				case "06 - NOTA DE CREDITO":
					formaPago.nota += compra.total - compra.porPagar;
					break;
				case "07 - MIXTO":
					formaPago.mixto += compra.total - compra.porPagar;
					break;
				default:
					break;
			}
		}
		for (const nota of notasCompra) {
			switch (nota.compra?.tipo) {
				case "producto":
					summaryCompras.producto_nota += nota.total;
					break
				case "item":
					summaryCompras.item_nota += nota.total;
					break
				case "suministro":
					summaryCompras.suministro_nota += nota.total;
					break
				default:
					break;
			}
		}

		setSummaries({
			ventas: ventasSummaries,
			compras: {
				...summaryCompras,
				contado: summaryCompras.producto_contado + summaryCompras.item_contado + summaryCompras.suministro_contado,
				credito: summaryCompras.producto_credito + summaryCompras.item_credito + summaryCompras.suministro_credito,
				nota: summaryCompras.producto_nota + summaryCompras.item_nota + summaryCompras.suministro_nota,
				formaPago,
			},
		});
    }, [recibos, facturas, compras, notasCompra]);

    useEffect(() => {
		getFacturas();
		getRecibos();
		getNotasCredito();
		getCompras();
		getNotasCompra();
		getCuentasPorPagar();
	}, []);

    useEffect(() => {
		if (finishedCallCount < 6) return;

        calculateSummaries();

        const timeout = setTimeout(() => {
			window.print();
		}, 1000);

		return () => {
			clearTimeout(timeout);
		};
	}, [finishedCallCount]);

	return useMemo(
		() => ({
			facturas,
			recibos,
			notas,
			summaries,
			cuentasPorPagar,
		}),
		[facturas, recibos, notas, summaries, cuentasPorPagar]
	);
};

const ReporteCuadreResumido = () => {
    const location = useLocation();
	const queryParams = new URLSearchParams(location.search);

    const user = auth.getUser();
    const sucursal = user.sucursales.filter(
		(suc) => suc.id === user.sucursalActual
	)[0];

    const { facturas, recibos, notas, summaries, cuentasPorPagar } = useData();

    const ValorTotalNotaCreditos = notas.reduce((acc, nota) => acc + (nota.estado === "cancelada" ? 0 : parseFloat(nota.total)), 0);
    const valorTotalFacturas = facturas.reduce((acc, factura) => acc + (['cancelada', 'procesando'].indexOf(factura.estado) > -1 ? 0 : parseFloat(factura.total)), 0);
    const ValorTotalRecibos = recibos.reduce((acc, recibo) => acc + (recibo.status === "cancelado" ? 0 : parseFloat(recibo.abono_total)), 0);
	const totalCxP = cuentasPorPagar.reduce((acc, cxp) => acc + (cxp.total - cxp.porPagar), 0);

    const vendedorId = queryParams.get("vendedor");
    let vendedor = {};
    if (vendedorId) {
        vendedor = facturas.find((factura) => factura.vendedor?.id === parseInt(vendedorId))?.vendedor;
    }

    return (<>
		<style>
			{`
			@import url('https://fonts.googleapis.com/css2?family=Saira+Extra+Condensed:wght@400;700&display=swap');
			@media (max-width: 450px) {
				body {
					font-family: "Saira Extra Condensed", sans-serif !important;
					width: 100% !important;
					font-size: 12pt !important;
				}

				@media print {
					body {
						min-width: auto !important;
					}
				}

				@page {
					size: 80mm auto;
					margin: 0;
				}

				@page {
					size: 80mm 300mm !important;
					margin-top: 0 !important;
					margin-bottom: 0 !important;
					margin-left: 20px !important;
					margin-right: 20px !important;
				}

				.invoice {
					width: 100% !important;
					padding: 0 20px 0 5px;
				}
				.invoice small {
					font-size: 10pt;
					line-height: 1;
				}
				.invoice-table {
					display: none;
				}
				.sm-invoice-table {
					display: block;
				}
				.invoice h3 {
					margin-bottom: 0.1rem;
				}
				.invoice h3.signature-title {
					font-weight: 600 !important;
				}
				.client-info-title {
					font-weight: 600 !important;
				}
				.info-cliente-responsive .info-client__response {
					line-height: 1.2 !important;
				}
				.invoice-name {
					font-weight: 600 !important;
				}
				.info-cliente-responsive {
					line-height: 1.2 !important;
					margin-bottom: 5px !important;
				}
				.info-cliente-responsive .row{
					margin-bottom: 6px;
				}
				.invoice-mobile {
					display: block;
					width: 100% !important;
				}
				hr {
					margin: 5px 0;
				}
				.sucursal-name {
					font-size: 14pt;
				}
				.detalles-sucursal {
					line-height: 1;
					margin-bottom: 10px;
				}
				.top-info {
					line-height: 1;
				}
				.sucursal-name-sm {
					display:block;
				}
				.producto-item {
					font-size: 10pt;
				}
				.factura-logo{
					display:none;
				}
				.sucursal-name-lg{
					display:none;
				}
				.info-cliente-responsive .row {
					margin-bottom: 0 !important;
				}
				.client-info-title {
					min-width: 90px;
				}
				.tabla-titulos small {
					font-weight: 600;
				}
				.totales {
					line-height: 1.2;
					margin-bottom: 10px;
				}
				.summary-text{
					// max-width:39% !important;
					width:auto !important;
					flex:auto;
					padding-left: 5px !important;
					padding-right: 2px !important;
				}
				.justify-content-between {
					justify-content: flex-end !important;
				}
				.justify-content-between :nth-child(2){
					max-width: 33% !important;
				}
			}`}
		</style>

		<div className="d-none d-md-block">
			<div className="row">
				<h2 className="col-md-12 text-center m-b-5">
					Reporte Cuadre de Caja Resumido
				</h2>
                <div className="col-md-12 text-center">
					{sucursal.nombre} - {sucursal.direccion} •{" "}
					{queryParams.get("desde")
						? `${dayjs(queryParams.get("desde")).format(
								"DD/MM/YYYY"
						  )} - `
						: ""}{" "}
					{dayjs(queryParams.get("hasta")).format("DD/MM/YYYY")}
				</div>

                {vendedorId && (
                    <div className="col-md-12 text-center">
                        <strong className="mr-1">Vendedor:</strong> {vendedor?.nombre} {vendedor?.apellido}
                    </div>
                )}
            </div>

            <div className="d-flex justify-content-center mt-4">
				<div className="row border" style={{fontSize:"0.9rem", width: "680px"}}>
                    <div className="col col-md-12">
						<h3 className="pt-3 pb-3 text-center border-bottom">Ventas</h3>
					</div>

                    <div className="col col-md-7 pb-3">
						<div className="col col-md-12">
							<b style={{fontSize: "18px"}}>Total Facturado</b> = <span className="float-right">{formatCurrency(summaries.ventas.facturado)}</span>
						</div>
						<div className="col col-md-12 border-top pt-4">
							<h5 className="my-1 text-center">INGRESOS</h5>
							<b>Facturas</b> = <span className="float-right">{formatCurrency(summaries.ventas.pagada)}</span><br/>
							<b>Recibos</b> = <span className="float-right">{formatCurrency(ValorTotalRecibos, "$")}</span><br/>
							<div className="border-top"><b>Total Ingresos</b> = <span className="float-right">{formatCurrency(summaries.ventas.pagada + ValorTotalRecibos, "$")}</span></div><br/>
						</div>
						<div className="col col-md-12" style={{paddingTop: "1.3rem"}}>
							<b>Ventas a crédito</b> = <span className="float-right">{formatCurrency(summaries.ventas.credito, "$")}</span><br/>
							<b>Notas crédito</b> = <span className="float-right">{formatCurrency(ValorTotalNotaCreditos, "$")}</span><br/>
						</div>
					</div>

                    <div className="col col-md-5">
						<h5 className="py-2 text-center">FORMA DE PAGO</h5>
						<b>Efectivo</b> = <span className="float-right">{formatCurrency(summaries.ventas.efectivo)}</span><br/>
						<b>Transferencia</b> = <span className="float-right">{formatCurrency(summaries.ventas.transferencia)}</span><br/>
						<b>Cheque</b> = <span className="float-right">{formatCurrency(summaries.ventas.cheque)}</span><br/>
						<b>Tarjeta</b> = <span className="float-right">{formatCurrency(summaries.ventas.tarjeta + summaries.ventas['tarjeta-debito'])}</span><br/>
						<b>Depósito</b> = <span className="float-right">{formatCurrency(summaries.ventas.deposito)}</span><br/>
						<b>Otro</b> = <span className="float-right">{formatCurrency(summaries.ventas.otro)}</span><br/>
					</div>

                    <div className="col col-md-7 pb-3">
						<div className="col">
                            <h5 className="py-2 text-center">TÉRMINOS DE PAGO</h5>
                            <b>CONTADO</b> = <span className="float-right">{formatCurrency(summaries.ventas.contado)}</span><br/>
                            <b>CRÉDITO</b> = <span className="float-right">{formatCurrency(summaries.ventas.credito)}</span><br/>
                            <b>CONTRA ENTREGA</b> = <span className="float-right">{formatCurrency(summaries.ventas.contraEntrega)}</span>
                        </div>
					</div>
                </div>
            </div>

			<div className="d-flex justify-content-center mt-3">
				<div className="row border" style={{fontSize:"0.9rem", width: "680px"}}>
					<div className="col col-md-12">
						<h3 className="pt-3 text-center">Compras</h3>
					</div>

					<table className='table print-productos m-b-10'>
                        <thead className="head-table-producto">
                            <tr>
                                <th></th>
                                <th>Al contado</th>
                                <th>A crédito</th>
                                <th>Nota crédito</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><b>Inventario de productos</b></td>
                                <td>
									{formatCurrency(summaries.compras.producto_contado, "$")}
								</td>
                                <td>
									{formatCurrency(summaries.compras.producto_credito, "$")}
                                </td>
                                <td>
									{formatCurrency(summaries.compras.producto_nota, "$")}
								</td>
                            </tr>
							<tr>
                                <td><b>Inventario interno</b></td>
                                <td>
									{formatCurrency(summaries.compras.item_contado, "$")}
								</td>
                                <td>
									{formatCurrency(summaries.compras.item_credito, "$")}
                                </td>
                                <td>
									{formatCurrency(summaries.compras.item_nota, "$")}
								</td>
                            </tr>
							<tr>
                                <td><b>Trabajo/Suministro</b></td>
                                <td>
									{formatCurrency(summaries.compras.suministro_contado, "$")}
								</td>
                                <td>
									{formatCurrency(summaries.compras.suministro_credito, "$")}
                                </td>
                                <td>
									{formatCurrency(summaries.compras.suministro_nota, "$")}
								</td>
                            </tr>
							<tr>
                                <td>
									<strong>Total</strong>
								</td>
                                <td>
									<strong>
										{formatCurrency(summaries.compras.contado, "$")}
									</strong>
								</td>
                                <td>
									<strong>
										{formatCurrency(summaries.compras.credito, "$")}
									</strong>
                                </td>
                                <td>
									<strong>
										{formatCurrency(summaries.compras.nota, "$")}
									</strong>
								</td>
                            </tr>
                        </tbody>
                    </table>

					<div className="col col-md-12">
						<h3 className="pt-3 text-center">FORMA DE PAGO</h3>
					</div>
					<div className="col col-md-6 border-top">
						<b>01 - EFECTIVO</b> = <span className="float-right">{formatCurrency(summaries.compras.formaPago.efectivo, "$")}</span><br/>
						<b>02 - CHEQUES/TRANSFERENCIAS</b> = <span className="float-right">{formatCurrency(summaries.compras.formaPago.transferencia, "$")}</span><br/>
						<b>03 - TARJETA DE CREDITO/DEBITO</b> = <span className="float-right">{formatCurrency(summaries.compras.formaPago.tarjeta, "$")}</span><br/>
						<b>04 - COMPRA A CREDITO</b> = <span className="float-right">{formatCurrency(summaries.compras.formaPago.credito, "$")}</span><br/>
					</div>
					<div className="col col-md-6 border-top">
						<b>05 - PERMUTA</b> = <span className="float-right">{formatCurrency(summaries.compras.formaPago.permuta, "$")}</span><br/>
						<b>06 - NOTA DE CREDITO</b> = <span className="float-right">{formatCurrency(summaries.compras.formaPago.nota, "$")}</span><br/>
						<b>07 - MIXTO</b> = <span className="float-right">{formatCurrency(summaries.compras.formaPago.mixto, "$")}</span><br/>
					</div>

					<div className="col col-md-6">
						<h5 className="pt-3 pb-1">
							Total Comprado: {formatCurrency(summaries.compras.contado + summaries.compras.credito + summaries.compras.nota, "$")}
						</h5>
						<h5 className="pt-1 pb-3">
							Total Pagado en CXP: {formatCurrency(totalCxP, "$")}
						</h5>
					</div>

					<div className="col col-md-6">
						<h3 className="pt-3 pb-0 mb-0 text-center">TOTAL DE PAGOS</h3>
						<h3 className="pt-0 pb-3 mb-0 text-center">{formatCurrency(summaries.compras.contado + summaries.compras.credito + summaries.compras.nota + totalCxP, "$")}</h3>
					</div>
				</div>
			</div>
        </div>

		<div className="invoice invoice-mobile recibo-print d-md-none">
			<div className="clear-both p-t-30 p-b-10">
				<div>
					<h3 className="sucursal-name text-center">Reporte Cuadre de Caja de Ventas</h3>
					<div className="text-center detalles-sucursal">
						{sucursal.nombre} <br />
						RNC: {sucursal.rnc} TEL.:{sucursal.telefono}<br />
						{sucursal.direccion}<br />
						{queryParams.get("desde")
							? `${dayjs(queryParams.get("desde")).format("DD/MM/YYYY")} - ` 
							: ""}
						{dayjs(queryParams.get("hasta")).format("DD/MM/YYYY")}
					</div>

					{vendedorId && (
						<div className="detalles-sucursal text-center">
							<strong className="mr-1">Vendedor:</strong> {vendedor?.nombre} {vendedor?.apellido}
						</div>
					)}
				</div>
			</div>

			<div className="mx-2">
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
				<div className="d-block bold tipo-factura text-center uppercase">VENTAS</div>
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

				<div>
					<div className="row producto-item align-items-end" style={{ marginTop: "5px" }}>
						<div className="col-6" style={{ lineHeight: "1" }}><strong>Total Facturado:</strong></div>
						<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.facturado)}</div>
					</div>
					<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
						<div className="col-6" style={{ lineHeight: "1" }}><strong>Cantidad Facturas:</strong></div>
						<div className="col text-right" style={{ lineHeight: "1" }}>{facturas.length}</div>
					</div>
				</div>
			</div>

			<div style={{height: '16px'}}></div>
			<div className="mx-2">
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
				<div className="d-block bold tipo-factura text-center uppercase">TÉRMINOS DE PAGO</div>
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

				<div className="row producto-item align-items-end" style={{ marginTop: "5px", marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Contado:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.contado)}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Crédito:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.credito)}</div>
				</div>
				<div className="row producto-item align-items-end">
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Contra Entrega:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.contraEntrega)}</div>
				</div>
			</div>

			<div style={{height: '16px'}}></div>
			<div className="mx-2">
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
				<div className="d-block bold tipo-factura text-center uppercase">INGRESOS</div>
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

				<div className="row producto-item align-items-end" style={{ marginTop: "5px", marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Facturas:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.pagada, "$")}</div>
				</div>
				<div className="row producto-item align-items-end">
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Recibos:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(ValorTotalRecibos, "$")}</div>
				</div>
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
				<div className="row producto-item align-items-end" style={{ marginTop: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Total Ingresos:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.pagada + ValorTotalRecibos, "$")}</div>
				</div>

				<div style={{height: '16px'}}></div>

				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Ventas a crédito:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.credito, "$")}</div>
				</div>

				<div className="row producto-item align-items-end">
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Notas crédito:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(ValorTotalNotaCreditos, "$")}</div>
				</div>
			</div>

			<div style={{height: '16px'}}></div>
			<div className="mx-2">
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
				<div className="d-block bold tipo-factura text-center uppercase">FORMA DE PAGO</div>
				<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

				<div className="row producto-item align-items-end" style={{ marginTop: "5px", marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Efectivo:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.efectivo)}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Transferencia:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.transferencia)}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Cheque:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.cheque)}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Tarjeta:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.tarjeta + summaries.ventas['tarjeta-debito'])}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Depósito:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.deposito)}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Otro:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.otro)}</div>
				</div>
			</div>
			
			<div style={{height: '16px'}}></div>
			<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
			<div className="d-block bold tipo-factura text-center uppercase">COMPRAS</div>
			<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

			<div className="row tabla-titulos" style={{ fontWeight: "600" }}>
				<div className="col-3"><small>Inventario</small></div>
				<div className="col-3"><small>Contado</small></div>
				<div className="col-3"><small>Crédito</small></div>
				<div className="col-3"><small>NC</small></div>
			</div>
			<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0 0 5px'}} />

			<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
				<div className="col-3" style={{ lineHeight: "1" }}><small>Producto</small></div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.producto_contado, "$")}</div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.producto_credito, "$")}</div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.producto_nota, "$")}</div>
			</div>

			<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
				<div className="col-3" style={{ lineHeight: "1" }}><small>Interno</small></div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.item_contado, "$")}</div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.item_credito, "$")}</div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.item_nota, "$")}</div>
			</div>

			<div className="row producto-item align-items-end">
				<div className="col-3" style={{ lineHeight: "1" }}><small>Suministro</small></div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.suministro_contado, "$")}</div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.suministro_credito, "$")}</div>
				<div className="col-3" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.suministro_nota, "$")}</div>
			</div>
			<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0 0 5px'}} />
			<div className="row producto-item align-items-end">
				<div className="col-3" style={{ lineHeight: "1" }}><strong>Total</strong></div>
				<div className="col-3" style={{ lineHeight: "1" }}><strong>{formatCurrency(summaries.compras.contado, "$")}</strong></div>
				<div className="col-3" style={{ lineHeight: "1" }}><strong>{formatCurrency(summaries.compras.credito, "$")}</strong></div>
				<div className="col-3" style={{ lineHeight: "1" }}><strong>{formatCurrency(summaries.compras.nota, "$")}</strong></div>
			</div>


			<div style={{height: '16px'}}></div>
			<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
			<div className="d-block bold tipo-factura text-center uppercase">FORMA DE PAGO</div>
			<hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
			<div className="mt-2">
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-7" style={{ lineHeight: "1" }}><strong>EFECTIVO:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.formaPago.efectivo, "$")}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-7" style={{ lineHeight: "1" }}><strong>CHEQUES/TRANSFERENCIAS:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.formaPago.transferencia, "$")}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-7" style={{ lineHeight: "1" }}><strong>TARJETA DE CREDITO/DEBITO:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.formaPago.tarjeta, "$")}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-7" style={{ lineHeight: "1" }}><strong>COMPRA A CREDITO:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.formaPago.credito, "$")}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-7" style={{ lineHeight: "1" }}><strong>PERMUTA:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.formaPago.permuta, "$")}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-7" style={{ lineHeight: "1" }}><strong>NOTA DE CREDITO:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.formaPago.nota, "$")}</div>
				</div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-7" style={{ lineHeight: "1" }}><strong>MIXTO:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.formaPago.mixto, "$")}</div>
				</div>
			</div>



			<div style={{height: '32px'}}></div>
			<div>
				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Total Comprado:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.contado + summaries.compras.credito + summaries.compras.nota, "$")}</div>
				</div>

				<div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
					<div className="col-6" style={{ lineHeight: "1" }}><strong>Total Pagado en CXP:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(totalCxP, "$")}</div>
				</div>

				<div className="row producto-item align-items-end">
					<div className="col-6" style={{ lineHeight: "1" }}><strong>TOTAL DE PAGOS:</strong></div>
					<div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.compras.contado + summaries.compras.credito + summaries.compras.nota + totalCxP, "$")}</div>
				</div>
			</div>

			<div style={{height: '16px'}}></div>
		</div>
    </>);
};

export default ReporteCuadreResumido;
